const TARGETS = new Set([
  'RunRecordsLightweight',
  'FirstBookDailyReviewPanel',
  'HistoryMonitor',
  'SystemSettingsLightweight'
])

const RUN_STATUSES = new Set(['success', 'partial_success', 'failed', 'running', 'pending'])
const BOOK_STATUSES = new Set([
  'pending', 'confirmed', 'partially_confirmed', 'stale_or_expired',
  'rejected', 'expired', 'closed'
])

function text(value) {
  return String(value === undefined || value === null ? '' : value).trim()
}

function signature(query) {
  return Object.keys(query || {}).sort().map(key => `${key}=${text(query[key])}`).join('&')
}

function beijingDateKey(value) {
  if (!value) return ''
  let raw = text(value).replace(' ', 'T')
  if (!/(Z|[+-]\d{2}:?\d{2})$/.test(raw)) raw += '+08:00'
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return ''
  const parts = new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit'
  }).formatToParts(date).reduce((result, item) => {
    result[item.type] = item.value
    return result
  }, {})
  return `${parts.year}-${parts.month}-${parts.day}`
}

function todayBeijingKey() {
  return beijingDateKey(new Date().toISOString())
}

function runTime(row) {
  return row && (
    row.started_at || row.scheduled_for || row.run_time_bj || row.run_time ||
    row.created_at || row.completed_at
  ) || ''
}

function parentId(row) {
  return text(row && (row.parent_run_id || row.diagnostic_id || row.run_id || row.log_id))
}

function notificationIds(row) {
  if (!row) return []
  return [
    row.parent_run_id, row.title, row.batch_log_id, row.task_run_id,
    row.run_id, row.log_id, row.diagnostic_id
  ].map(text).filter(Boolean)
}

function sameRows(left, right, keyBuilder) {
  if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false
  return left.every((item, index) => keyBuilder(item) === keyBuilder(right[index]))
}

function clearFocus(root) {
  if (!root || !root.querySelectorAll) return
  root.querySelectorAll('.dashboard-deep-link-focus, .dashboard-deep-link-row-match')
    .forEach(node => {
      node.classList.remove('dashboard-deep-link-focus')
      node.classList.remove('dashboard-deep-link-row-match')
    })
}

function installStyle() {
  if (document.getElementById('dashboard-deep-link-receiver-style')) return
  const style = document.createElement('style')
  style.id = 'dashboard-deep-link-receiver-style'
  style.textContent = `
    .dashboard-deep-link-context{display:flex;align-items:flex-start;gap:10px;margin-bottom:14px;padding:11px 13px;border:1px solid #bfdbfe;border-radius:12px;background:#eff6ff;color:#334155;font-size:12px;line-height:1.55}
    .dashboard-deep-link-context strong{flex:0 0 auto;color:#1d4ed8}
    .dashboard-deep-link-context code{min-width:0;color:#1d4ed8;font-family:Consolas,Monaco,monospace;white-space:normal;overflow-wrap:anywhere;word-break:break-all}
    .dashboard-deep-link-focus{outline:2px solid rgba(37,99,235,.45)!important;outline-offset:3px;box-shadow:0 0 0 6px rgba(37,99,235,.08)!important}
    .dashboard-deep-link-row-match td{background:#fff7ed!important}
  `
  document.head.appendChild(style)
}

export default {
  install(Vue) {
    installStyle()

    Vue.mixin({
      created() {
        if (!TARGETS.has(this.$options.name)) return
        const schedule = () => this.__scheduleDashboardDeepLink()
        this.__dashboardDeepLinkUnwatchers = [
          this.$watch(() => this.$route && this.$route.query, schedule, { deep: true }),
          this.$watch(() => this.loading, value => {
            if (value) this.__resetDashboardDeepLinkBases()
            schedule()
          }),
          this.$watch(() => this.notificationLoading, value => {
            if (value) this.__dashboardDeepLinkNotificationBase = null
            schedule()
          }),
          this.$watch(() => Array.isArray(this.parentRuns) ? this.parentRuns.length : -1, schedule),
          this.$watch(() => Array.isArray(this.notificationHistory) ? this.notificationHistory.length : -1, schedule),
          this.$watch(() => Array.isArray(this.changeSets) ? this.changeSets.length : -1, schedule),
          this.$watch(() => this.payload && this.payload.as_of, schedule),
          this.$watch(() => this.dataSourceChecking, schedule)
        ]
      },
      mounted() {
        if (TARGETS.has(this.$options.name)) this.__scheduleDashboardDeepLink()
      },
      beforeDestroy() {
        if (this.__dashboardDeepLinkTimer) clearTimeout(this.__dashboardDeepLinkTimer)
        ;(this.__dashboardDeepLinkUnwatchers || []).forEach(unwatch => {
          if (typeof unwatch === 'function') unwatch()
        })
      },
      methods: {
        __resetDashboardDeepLinkBases() {
          this.__dashboardDeepLinkParentBase = null
          this.__dashboardDeepLinkNotificationBase = null
          this.__dashboardDeepLinkChangeSetBase = null
        },
        __scheduleDashboardDeepLink() {
          if (!TARGETS.has(this.$options.name)) return
          if (this.__dashboardDeepLinkTimer) clearTimeout(this.__dashboardDeepLinkTimer)
          this.__dashboardDeepLinkTimer = setTimeout(() => {
            this.$nextTick(() => this.__applyDashboardDeepLink())
          }, 30)
        },
        __applyDashboardDeepLink() {
          if (this.loading === true || this.notificationLoading === true || this.dataSourceChecking === true) return
          const name = this.$options.name
          if (name === 'RunRecordsLightweight') this.__applyRunRecordsDeepLink()
          if (name === 'FirstBookDailyReviewPanel') this.__applyFirstBookDeepLink()
          if (name === 'HistoryMonitor') this.__applyHistoryDeepLink()
          if (name === 'SystemSettingsLightweight') this.__applySettingsDeepLink()
        },
        __setDashboardDeepLinkBanner(label, identifier) {
          const root = this.$el
          if (!root || !root.insertBefore) return
          let banner = root.querySelector('[data-dashboard-deep-link-context]')
          if (!label && !identifier) {
            if (banner && banner.parentNode) banner.parentNode.removeChild(banner)
            return
          }
          if (!banner) {
            banner = document.createElement('div')
            banner.setAttribute('data-dashboard-deep-link-context', 'true')
            banner.className = 'dashboard-deep-link-context'
            banner.appendChild(document.createElement('strong'))
            banner.appendChild(document.createElement('code'))
            root.insertBefore(banner, root.firstChild)
          }
          banner.querySelector('strong').textContent = label || '首页深链'
          banner.querySelector('code').textContent = identifier || '已按首页条件定位'
        },
        __applyRunRecordsDeepLink() {
          const query = this.$route && this.$route.query || {}
          const section = query.section === 'notifications' ? 'notifications' : 'runs'
          const group = text(query.group)
          const status = text(query.status).toLowerCase()
          const exactId = text(query.parent_run_id)
          const dateFilter = text(query.date).toLowerCase()

          this.activeSection = section
          if (this.filters) {
            this.filters.group = group
            this.filters.status = RUN_STATUSES.has(status) ? status : ''
            if (exactId) this.filters.keyword = exactId
          }

          if (!this.__dashboardDeepLinkParentBase) {
            this.__dashboardDeepLinkParentBase = Array.isArray(this.parentRuns) ? this.parentRuns.slice() : []
          }
          if (!this.__dashboardDeepLinkNotificationBase) {
            this.__dashboardDeepLinkNotificationBase = Array.isArray(this.notificationHistory)
              ? this.notificationHistory.slice()
              : []
          }

          const parentBase = this.__dashboardDeepLinkParentBase
          const visibleRuns = dateFilter === 'today'
            ? parentBase.filter(row => beijingDateKey(runTime(row)) === todayBeijingKey())
            : parentBase.slice()
          if (!sameRows(this.parentRuns, visibleRuns, parentId)) this.parentRuns = visibleRuns

          let visibleNotifications = this.__dashboardDeepLinkNotificationBase.slice()
          if (section === 'notifications' && RUN_STATUSES.has(status)) {
            visibleNotifications = visibleNotifications.filter(row => text(row.status).toLowerCase() === status)
          }
          if (section === 'notifications' && exactId) {
            visibleNotifications = visibleNotifications.filter(row => notificationIds(row).includes(exactId))
          }
          if (!sameRows(this.notificationHistory, visibleNotifications, row => notificationIds(row).join('|'))) {
            this.notificationHistory = visibleNotifications
          }

          const labels = ['运行记录深链', '父任务级']
          if (dateFilter === 'today') labels.push('今日')
          if (group) labels.push(group)
          if (status) labels.push(status)
          this.__setDashboardDeepLinkBanner(signature(query) ? labels.join(' · ') : '', exactId)

          const openKey = `${exactId}:${text(query.open)}`
          if (
            exactId && text(query.open).toLowerCase() === 'detail' &&
            this.__dashboardDeepLinkOpenedKey !== openKey && typeof this.showDetail === 'function'
          ) {
            this.__dashboardDeepLinkOpenedKey = openKey
            const row = parentBase.find(item => text(item.parent_run_id) === exactId)
            this.showDetail(row || { parent_run_id: exactId })
          }
        },
        __applyFirstBookDeepLink() {
          const query = this.$route && this.$route.query || {}
          const tab = text(query.tab).toLowerCase()
          const status = text(query.status).toLowerCase()
          const changeSetId = text(query.change_set_id)

          if (tab === 'changes' || tab === 'plan') this.activeTab = tab
          if (!this.__dashboardDeepLinkChangeSetBase) {
            this.__dashboardDeepLinkChangeSetBase = Array.isArray(this.changeSets) ? this.changeSets.slice() : []
          }
          const visible = BOOK_STATUSES.has(status)
            ? this.__dashboardDeepLinkChangeSetBase.filter(row => text(row.status).toLowerCase() === status)
            : this.__dashboardDeepLinkChangeSetBase.slice()
          if (!sameRows(this.changeSets, visible, row => text(row.change_set_id))) this.changeSets = visible

          const label = signature(query)
            ? `第一本账深链 · ${tab === 'changes' ? '建议记录' : '今日计划'}${status ? ` · ${status}` : ''}`
            : ''
          this.__setDashboardDeepLinkBanner(label, changeSetId)

          const openKey = `${changeSetId}:${text(query.open)}`
          if (changeSetId && this.__dashboardDeepLinkOpenedKey !== openKey && typeof this.openDetail === 'function') {
            this.__dashboardDeepLinkOpenedKey = openKey
            this.activeTab = 'changes'
            this.openDetail(changeSetId)
          }
        },
        __applyHistoryDeepLink() {
          const query = this.$route && this.$route.query || {}
          const section = text(query.section).toLowerCase()
          const status = text(query.status).toLowerCase()
          const active = section === 'coverage'
          this.__setDashboardDeepLinkBanner(
            active ? `历史库深链 · 覆盖进度${status ? ` · ${status}` : ''}` : '',
            text(query.diagnosis_id)
          )
          clearFocus(this.$el)
          if (!active || !this.$el || !this.$el.querySelectorAll) return

          const target = Array.from(this.$el.querySelectorAll('.section-card'))
            .find(card => text(card.textContent).includes('覆盖进度'))
          if (!target) return
          target.classList.add('dashboard-deep-link-focus')

          if (status) {
            const aliases = status === 'pending'
              ? ['待继续', '待接入', '待确认', 'missing', '运行中', '失败']
              : [status]
            target.querySelectorAll('.el-table__body tr').forEach(row => {
              const rowText = text(row.textContent).toLowerCase()
              if (aliases.some(alias => rowText.includes(alias.toLowerCase()))) {
                row.classList.add('dashboard-deep-link-row-match')
              }
            })
          }

          const key = signature(query)
          if (this.__dashboardDeepLinkScrolledSignature !== key) {
            this.__dashboardDeepLinkScrolledSignature = key
            target.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        },
        __applySettingsDeepLink() {
          const query = this.$route && this.$route.query || {}
          const tab = text(query.tab).toLowerCase()
          const source = text(query.source).toLowerCase()
          if (['notification', 'datasource', 'info'].includes(tab)) this.activeTab = tab
          this.__setDashboardDeepLinkBanner(
            signature(query) ? `系统设置深链 · ${tab || 'notification'}${source ? ` · ${source}` : ''}` : '',
            source
          )
          clearFocus(this.$el)
          if (tab !== 'datasource' || !source || !this.$el || !this.$el.querySelectorAll) return

          const target = Array.from(this.$el.querySelectorAll('.status-card, .setting-card, .el-descriptions'))
            .find(node => text(node.textContent).toLowerCase().includes(source))
          if (!target) return
          target.classList.add('dashboard-deep-link-focus')

          const key = signature(query)
          if (this.__dashboardDeepLinkScrolledSignature !== key) {
            this.__dashboardDeepLinkScrolledSignature = key
            target.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        }
      }
    })
  }
}
